import { useEffect, useRef, useState, useCallback } from "react";
import Hls from "hls.js";
import api from "@/lib/api";
import { RotateCcw, Settings, Check } from "lucide-react";

/**
 * Adaptive HLS player for studio-uploaded videos (Cloudflare Stream).
 * Mirrors BrandedPlayer's analytics: view_start / heartbeat / view_complete
 * events to /events/view and resume progress to /me/progress.
 */
const HEARTBEAT_MS = 15000;

function randomId() {
  try {
    return crypto.randomUUID();
  } catch (_e) {
    return "sess-" + Math.random().toString(36).slice(2) + Date.now();
  }
}

export default function CloudflareHlsPlayer({
  tenant,
  tenantSlug,
  content,
  episodeId,
  videoSource,
  initialPositionSeconds = 0,
}) {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [ended, setEnded] = useState(false);
  const [levels, setLevels] = useState([]);       // [{i, height, bitrate}]
  const [chosenLevel, setChosenLevel] = useState(-1); // -1 = Auto
  const [activeLevel, setActiveLevel] = useState(-1); // current rendition in Auto
  const [qualityOpen, setQualityOpen] = useState(false);
  const state = useRef({
    position: initialPositionSeconds || 0,
    duration: 0,
    sessionId: randomId(),
    startedFired: false,
    completeFired: false,
    lastEventAt: 0,
    lastSavedAt: 0,
  });

  const accent = tenant?.accent_color || "#F59E0B";
  const hls = videoSource?.hls;
  const poster = videoSource?.thumbnail || content?.hero_banner_url || content?.hero_image_url || "";

  const fireEvent = useCallback(async (kind) => {
    if (!tenantSlug || !content?.id) return;
    try {
      await api.post("/events/view", {
        tenant_slug: tenantSlug,
        content_item_id: content.id,
        episode_id: episodeId || undefined,
        source: content.kind === "series" ? "series" : (content.kind === "reel" ? "reel" : "movie"),
        kind,
        position_seconds: Math.floor(state.current.position || 0),
        duration_seconds: Math.floor(state.current.duration || 0),
        session_id: state.current.sessionId,
      });
    } catch (_e) { /* best-effort */ }
  }, [tenantSlug, content?.id, content?.kind, episodeId]);

  const saveProgress = useCallback(async () => {
    const s = state.current;
    if (!content?.id || s.duration <= 0) return;
    try {
      await api.post("/me/progress", {
        content_item_id: content.id,
        episode_id: episodeId || null,
        position_seconds: Math.floor(s.position),
        duration_seconds: Math.floor(s.duration),
        completed: s.duration > 0 && s.position / s.duration >= 0.95,
      });
    } catch (_e) { /* silent */ }
  }, [content?.id, episodeId]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !hls) return;
    let hlsInstance;
    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = hls; // Safari native HLS (quality handled by the OS player)
    } else if (Hls.isSupported()) {
      // capLevelToPlayerSize:false => never downgrade resolution just because the
      // player element is small; always allow the highest rendition Cloudflare offers.
      hlsInstance = new Hls({ enableWorker: true, lowLatencyMode: false, capLevelToPlayerSize: false });
      hlsRef.current = hlsInstance;
      hlsInstance.on(Hls.Events.MANIFEST_PARSED, () => {
        const lv = (hlsInstance.levels || []).map((l, i) => ({ i, height: l.height || 0, bitrate: l.bitrate || 0 }));
        setLevels(lv);
      });
      hlsInstance.on(Hls.Events.LEVEL_SWITCHED, (_e, data) => setActiveLevel(data.level));
      hlsInstance.loadSource(hls);
      hlsInstance.attachMedia(video);
    } else {
      video.src = hls;
    }

    const seed = Math.floor(initialPositionSeconds || 0);
    const onLoaded = () => {
      state.current.duration = video.duration || 0;
      if (seed > 0 && seed < (video.duration || Infinity)) {
        try { video.currentTime = seed; } catch (_e) { /* noop */ }
      }
    };
    const onPlay = () => {
      if (!state.current.startedFired) {
        state.current.startedFired = true;
        fireEvent("view_start");
      }
    };
    const onTimeUpdate = () => {
      const s = state.current;
      s.position = video.currentTime || 0;
      s.duration = video.duration || s.duration;
      const now = Date.now();
      if (now - s.lastEventAt > HEARTBEAT_MS) {
        s.lastEventAt = now;
        fireEvent("heartbeat");
      }
      if (now - s.lastSavedAt > 8000) {
        s.lastSavedAt = now;
        saveProgress();
      }
      if (!s.completeFired && s.duration > 0 && s.position / s.duration >= 0.95) {
        s.completeFired = true;
        fireEvent("view_complete");
      }
    };
    const onEnded = () => {
      state.current.completeFired = true;
      saveProgress();
      setEnded(true);
    };

    video.addEventListener("loadedmetadata", onLoaded);
    video.addEventListener("play", onPlay);
    video.addEventListener("timeupdate", onTimeUpdate);
    video.addEventListener("ended", onEnded);

    return () => {
      saveProgress();
      video.removeEventListener("loadedmetadata", onLoaded);
      video.removeEventListener("play", onPlay);
      video.removeEventListener("timeupdate", onTimeUpdate);
      video.removeEventListener("ended", onEnded);
      if (hlsInstance) hlsInstance.destroy();
      hlsRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hls]);

  const replay = () => {
    setEnded(false);
    const v = videoRef.current;
    if (v) { v.currentTime = 0; v.play(); }
  };

  const selectQuality = (i) => {
    setChosenLevel(i);
    setQualityOpen(false);
    if (hlsRef.current) hlsRef.current.currentLevel = i; // -1 = auto
  };

  const sortedLevels = [...levels].sort((a, b) => b.height - a.height);
  const maxHeight = sortedLevels[0]?.height || 0;
  const currentLabel =
    chosenLevel === -1
      ? `Auto${activeLevel >= 0 && levels[activeLevel]?.height ? ` · ${levels[activeLevel].height}p` : ""}`
      : `${levels[chosenLevel]?.height || ""}p`;

  if (!hls) {
    return (
      <div className="aspect-video bg-black border border-white/10 grid place-items-center text-white/50 text-sm">
        Video is still processing — check back in a moment.
      </div>
    );
  }

  return (
    <div className="relative aspect-video bg-black border border-white/10 overflow-hidden" data-testid="cf-hls-player">
      <video
        ref={videoRef}
        controls
        playsInline
        poster={poster}
        className="w-full h-full bg-black"
        controlsList="nodownload"
      />

      {/* Quality selector (hls.js only; Safari uses native controls) */}
      {levels.length > 1 && !ended && (
        <div className="absolute top-3 right-3 z-10" data-testid="cf-hls-quality">
          <button
            type="button"
            onClick={() => setQualityOpen((o) => !o)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-black/70 backdrop-blur-md text-white/90 text-[11px] font-medium tracking-wide rounded hover:bg-black/85 transition-colors"
            data-testid="cf-hls-quality-button"
          >
            <Settings className="w-3.5 h-3.5" />
            {currentLabel}
            {maxHeight >= 1080 && <span className="ml-1 px-1 py-0.5 text-[8px] font-bold leading-none bg-white/15 rounded">HD</span>}
          </button>
          {qualityOpen && (
            <div className="absolute right-0 mt-1.5 min-w-[120px] bg-black/90 backdrop-blur-md rounded border border-white/10 py-1 shadow-xl" data-testid="cf-hls-quality-menu">
              <QualityItem label="Auto" active={chosenLevel === -1} onClick={() => selectQuality(-1)} />
              {sortedLevels.map((l) => (
                <QualityItem
                  key={l.i}
                  label={`${l.height}p`}
                  active={chosenLevel === l.i}
                  onClick={() => selectQuality(l.i)}
                  testid={`cf-hls-quality-${l.height}`}
                />
              ))}
            </div>
          )}
        </div>
      )}
      {ended && (
        <div className="absolute inset-0 bg-black/85 flex flex-col items-center justify-center gap-5 text-center px-6" data-testid="cf-hls-ended">
          <div className="text-[11px] uppercase tracking-[0.3em] text-white/50">You finished</div>
          <h3 className="font-display font-black text-2xl md:text-4xl tracking-tight text-white max-w-2xl">
            {content?.title}
          </h3>
          <button
            onClick={replay}
            className="inline-flex items-center gap-2 px-6 py-3 text-black font-semibold uppercase text-xs tracking-[0.25em]"
            style={{ background: accent }}
            data-testid="cf-hls-replay"
          >
            <RotateCcw className="w-4 h-4" /> Replay
          </button>
        </div>
      )}
    </div>
  );
}

function QualityItem({ label, active, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center justify-between gap-3 px-3 py-1.5 text-[12px] text-left transition-colors ${active ? "text-white" : "text-white/60 hover:text-white hover:bg-white/5"}`}
      data-testid={testid}
    >
      {label}
      {active && <Check className="w-3.5 h-3.5" />}
    </button>
  );
}
